import React, { useState, useMemo, useEffect } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Check, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  buildPresets,
  detectPreset,
  formatRangeLabel,
  fyRange,
  fyQuarterRange,
  monthRange,
  fyStartYear,
  fyLabel,
} from "@/lib/dateFilter";

const FY_OPTIONS = (() => {
  const cur = fyStartYear(new Date());
  return [cur, cur - 1, cur - 2, cur - 3];
})();

const QUARTER_LABELS = ["Q1 (Apr–Jun)", "Q2 (Jul–Sep)", "Q3 (Oct–Dec)", "Q4 (Jan–Mar)"];

const MONTHS = [
  ["Apr", 3],
  ["May", 4],
  ["Jun", 5],
  ["Jul", 6],
  ["Aug", 7],
  ["Sep", 8],
  ["Oct", 9],
  ["Nov", 10],
  ["Dec", 11],
  ["Jan", 0],
  ["Feb", 1],
  ["Mar", 2],
];

// Calendar styling — extracted so the object reference is stable across renders.
const CAL_CLASSNAMES = {
  day_today: "ring-1 ring-[#E76F1A]/40 text-[#1F3A5F] font-semibold",
  day_range_start:
    "day-range-start bg-[#1F3A5F] text-white rounded-l-md hover:bg-[#15263F]",
  day_range_end:
    "day-range-end bg-[#1F3A5F] text-white rounded-r-md hover:bg-[#15263F]",
  day_range_middle:
    "aria-selected:bg-[#E76F1A]/15 aria-selected:text-[#1F3A5F]",
  day_selected:
    "bg-[#1F3A5F] text-white hover:bg-[#15263F] focus:bg-[#15263F]",
  day_outside:
    "day-outside text-muted-foreground/30 opacity-40 aria-selected:bg-transparent aria-selected:text-muted-foreground/30",
  cell:
    "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:not(:has(.day-outside))]:[&:has([aria-selected])]:bg-[#E76F1A]/15 [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-range-start)]:rounded-l-md",
};

const PLACEHOLDER = "__pick__";

export const DateFilter = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [draftStart, setDraftStart] = useState(value.start_date);
  const [draftEnd, setDraftEnd] = useState(value.end_date);
  const [fy, setFy] = useState(PLACEHOLDER);
  const [quarterPick, setQuarterPick] = useState(PLACEHOLDER);
  const [monthPick, setMonthPick] = useState(PLACEHOLDER);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 640 : false
  );
  const [showCalendar, setShowCalendar] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (open) {
      setDraftStart(value.start_date);
      setDraftEnd(value.end_date);
      setFy(PLACEHOLDER);
      setQuarterPick(PLACEHOLDER);
      setMonthPick(PLACEHOLDER);
      // Desktop always shows the calendar; mobile collapses it by default.
      setShowCalendar(typeof window !== "undefined" ? window.innerWidth >= 640 : true);
    }
  }, [open, value.start_date, value.end_date]);

  const presets = useMemo(() => buildPresets(), []);
  const activePreset = useMemo(
    () => detectPreset(draftStart, draftEnd),
    [draftStart, draftEnd]
  );

  const triggerLabel = useMemo(
    () => formatRangeLabel(value.start_date, value.end_date),
    [value.start_date, value.end_date]
  );

  const setRange = (start, end) => {
    setDraftStart(start ? format(start, "yyyy-MM-dd") : null);
    setDraftEnd(end ? format(end, "yyyy-MM-dd") : null);
  };

  const onPickPreset = (p) => {
    const [s, e] = p.range();
    setRange(s, e);
    setFy(PLACEHOLDER);
    setQuarterPick(PLACEHOLDER);
    setMonthPick(PLACEHOLDER);
  };

  const onCalendarSelect = (r) => {
    setDraftStart(r?.from ? format(r.from, "yyyy-MM-dd") : null);
    setDraftEnd(r?.to ? format(r.to, "yyyy-MM-dd") : null);
    setFy(PLACEHOLDER);
    setQuarterPick(PLACEHOLDER);
    setMonthPick(PLACEHOLDER);
  };

  const onPickFY = (yStr) => {
    if (yStr === PLACEHOLDER) return;
    const y = Number(yStr);
    setFy(yStr);
    const [s, e] = fyRange(y);
    setRange(s, e);
    setQuarterPick(PLACEHOLDER);
    setMonthPick(PLACEHOLDER);
  };

  const onPickQuarter = (qStr) => {
    if (qStr === PLACEHOLDER) return;
    const baseFy = fy === PLACEHOLDER ? FY_OPTIONS[0] : Number(fy);
    const q = Number(qStr);
    setQuarterPick(qStr);
    setFy(String(baseFy));
    const [s, e] = fyQuarterRange(baseFy, q);
    setRange(s, e);
    setMonthPick(PLACEHOLDER);
  };

  const onPickMonth = (mStr) => {
    if (mStr === PLACEHOLDER) return;
    const baseFy = fy === PLACEHOLDER ? FY_OPTIONS[0] : Number(fy);
    const monthIdx = Number(mStr);
    setMonthPick(mStr);
    setFy(String(baseFy));
    // Apr-Dec belong to baseFy; Jan-Mar belong to baseFy+1 calendar year
    const calYear = monthIdx >= 3 ? baseFy : baseFy + 1;
    const [s, e] = monthRange(calYear, monthIdx);
    setRange(s, e);
    setQuarterPick(PLACEHOLDER);
  };

  const onApply = () => {
    onChange({ start_date: draftStart, end_date: draftEnd });
    setOpen(false);
  };

  const onCancel = () => {
    setOpen(false);
  };

  const onClear = () => {
    setRange(null, null);
  };

  const calRange = {
    from: draftStart ? new Date(draftStart) : undefined,
    to: draftEnd ? new Date(draftEnd) : undefined,
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-9 gap-2 font-medium min-w-[200px] justify-start"
          data-testid="filter-date-range"
        >
          <CalendarIcon className="w-4 h-4 shrink-0" />
          <span className="tabular text-xs truncate">{triggerLabel}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 overflow-hidden"
        style={{ width: "min(680px, calc(100vw - 1rem))" }}
        align="start"
        sideOffset={6}
      >
        <div className="flex flex-col sm:flex-row max-h-[80vh] overflow-y-auto">
          {/* Shortcuts */}
          <aside className="w-full sm:w-[180px] sm:border-r border-b sm:border-b-0 border-border bg-secondary/40 p-2" data-testid="date-shortcuts">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground px-2 py-1.5">
              Quick range
            </div>
            <ul className="grid grid-cols-2 sm:grid-cols-1 gap-0.5 sm:space-y-0.5">
              {presets.map((p) => {
                const active = activePreset === p.id;
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => onPickPreset(p)}
                      className={`w-full text-left text-xs rounded-md px-2.5 py-1.5 flex items-center justify-between transition-colors ${
                        active
                          ? "bg-[#1F3A5F] text-white"
                          : "hover:bg-background"
                      }`}
                      data-testid={`date-preset-${p.id}`}
                    >
                      <span className="truncate">{p.label}</span>
                      {active && <Check className="w-3.5 h-3.5 shrink-0" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>

          {/* Calendar + Selectors */}
          <div className="p-3 min-w-0 flex-1 flex flex-col">
            {isMobile && (
              <button
                type="button"
                onClick={() => setShowCalendar((v) => !v)}
                className="mb-3 inline-flex items-center justify-between gap-2 w-full rounded-md border border-border bg-secondary/40 hover:bg-secondary px-3 py-2 text-xs font-medium"
                data-testid="date-custom-toggle"
              >
                <span className="inline-flex items-center gap-2">
                  <CalendarIcon className="w-3.5 h-3.5" />
                  Custom dates
                </span>
                {showCalendar ? (
                  <ChevronUp className="w-3.5 h-3.5" />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5" />
                )}
              </button>
            )}

            {showCalendar && (
              <>
                <Calendar
                  mode="range"
                  selected={calRange}
                  onSelect={onCalendarSelect}
                  numberOfMonths={isMobile ? 1 : 2}
                  defaultMonth={calRange.from || new Date()}
                  showOutsideDays={true}
                  classNames={CAL_CLASSNAMES}
                />

                <div className="mt-3 grid grid-cols-3 gap-2">
                  <Select value={fy} onValueChange={onPickFY}>
                    <SelectTrigger className="h-9 text-xs" data-testid="date-fy-select">
                      <SelectValue placeholder="Financial Year" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={PLACEHOLDER} disabled>
                        Financial Year
                      </SelectItem>
                      {FY_OPTIONS.map((y) => (
                        <SelectItem key={y} value={String(y)}>
                          {fyLabel(y)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={quarterPick} onValueChange={onPickQuarter}>
                    <SelectTrigger className="h-9 text-xs" data-testid="date-quarter-select">
                      <SelectValue placeholder="Quarter" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={PLACEHOLDER} disabled>
                        Quarter
                      </SelectItem>
                      {QUARTER_LABELS.map((q, i) => (
                        <SelectItem key={i + 1} value={String(i + 1)}>
                          {q}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={monthPick} onValueChange={onPickMonth}>
                    <SelectTrigger className="h-9 text-xs" data-testid="date-month-select">
                      <SelectValue placeholder="Month" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={PLACEHOLDER} disabled>
                        Month
                      </SelectItem>
                      {MONTHS.map(([name, idx]) => (
                        <SelectItem key={idx} value={String(idx)}>
                          {name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div className="mt-auto pt-3 border-t border-border flex items-center justify-between gap-2 flex-wrap">
              <div
                className="text-xs text-[#1F3A5F] tabular font-medium"
                data-testid="date-selected-range"
              >
                {draftStart && draftEnd
                  ? `${format(new Date(draftStart), "d MMM yyyy")} – ${format(
                      new Date(draftEnd),
                      "d MMM yyyy"
                    )}`
                  : "All time"}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={onClear}
                  data-testid="date-clear"
                >
                  Clear
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={onCancel}
                  data-testid="date-cancel"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="h-8 text-xs bg-[#1F3A5F] hover:bg-[#15263F] text-white"
                  onClick={onApply}
                  data-testid="date-apply"
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
